import { Fragment, useMemo, useState } from "react"
import { useAllProjectsListQuery } from "@/api/graphql"

// Icons
import { CheckIcon, SelectorIcon } from "@/lib/icons"

// UI
import { cn } from "@/lib/utils"
import {
	Combobox,
	ComboboxInput,
	ComboboxButton,
	ComboboxOptions,
	ComboboxOption,
} from "@/lib/ui"

// I18n
import { useTrans } from "@/lib/i18n"

function useCurrentFinanceProject({ projectId }: { projectId?: number }) {
	const { data } = useAllProjectsListQuery()

	if (projectId) {
		return data?.projects?.find(
			(project) => project.id === String(projectId),
		)
	}
	return null
}

/**
 * ProjectSelector
 * @param param0
 * @returns
 */
interface ProjectSelectorProps {
	projectId?: number
	onChange: (id: string) => void
}
export function ProjectSelector({ projectId, onChange }: ProjectSelectorProps) {
	const { data } = useAllProjectsListQuery()
	const currentProject = useCurrentFinanceProject({ projectId })
	const [query, setQuery] = useState<string>(currentProject?.name ?? "")
	const t = useTrans("common")

	const sortedProjects = useMemo(() => {
		return (
			data?.projects?.sort(
				(a, b) => a.name?.localeCompare(b?.name ?? "") ?? 0,
			) ?? []
		)
	}, [data?.projects])

	const filteredProjects =
		query === "" || query === currentProject?.name
			? sortedProjects
			: sortedProjects?.filter((project) => {
					return project?.name
						?.toLowerCase()
						.includes(query.toLowerCase())
				})

	return (
		<Combobox
			as={Fragment}
			value={currentProject?.id}
			onChange={(value) => {
				// Get project name
				const nextProject = data?.projects?.find(
					(project) => project.id === value,
				)?.name

				if (nextProject) {
					setQuery(nextProject)
				}

				// Call onChange
				if (value) onChange(value)
			}}
		>
			<div className="relative mt-1 w-full max-w-xs self-end">
				<div className="relative w-full">
					<ComboboxButton
						className="w-full"
						aria-label={t("common:project_selector.open")}
					>
						<ComboboxInput
							className="w-full"
							aria-label={t("common:project_selector.label")}
							onChange={(e) => {
								setQuery(e.currentTarget.value)
							}}
							displayValue={(projectId: unknown) => {
								return (
									data?.projects?.find(
										(project) => project.id === projectId,
									)?.name ?? query
								)
							}}
						/>
						<div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
							<SelectorIcon
								className="size-5 text-gray-400"
								aria-hidden="true"
							/>
						</div>
					</ComboboxButton>
				</div>

				{filteredProjects && filteredProjects?.length > 0 && (
					<ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
						{filteredProjects.map((project) => (
							<ComboboxOption
								key={project?.id}
								value={project?.id}
								className={({ active }) =>
									cn(
										"relative cursor-pointer py-2 pr-9 pl-3 select-none",
										active
											? "bg-primary-500"
											: "text-gray-900",
									)
								}
							>
								{({ selected }) => (
									<>
										<div className="flex">
											<span
												className={cn(
													"truncate",
													selected && "font-semibold",
												)}
											>
												{project?.name}
											</span>
										</div>

										{selected && (
											<span className="text-black-500 absolute inset-y-0 right-0 flex items-center pr-4">
												<CheckIcon
													className="size-5"
													aria-hidden="true"
												/>
											</span>
										)}
									</>
								)}
							</ComboboxOption>
						))}
					</ComboboxOptions>
				)}
			</div>
		</Combobox>
	)
}
